import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { BasicModel } from 'src/packages/mitsBasics';
import { AccessGuard } from 'src/app/providers/access.guard';
import { FunctionSwitchHelperService } from '../component-helpers/function-switch.service';
import { ToggleMenuHelper } from '../component-helpers/toggle-menu-helper.service';
import { IDataService } from 'src/packages/mitsBasics';
import { OfflineDataService } from 'src/packages/mitsBasics';
import { ToastService } from 'src/packages/mitsBasics';
import { ErrorService } from 'src/packages/mitsBasics';
import { PageService } from './page.service';

@Injectable({
  providedIn: 'root',
})
export abstract class DetailPageService<
  T extends BasicModel,
> extends PageService {
  /**
   * Object to request a specific object through a specific service.
   * @deprecated - use objectForm for data and validation handling
   */
  object!: T;
  /** Object to create a specific form for validation */
  objectForm!: FormGroup;
  /** Is the form and data initialized */
  _initialized = false;

  /** Service for the object data. */
  public abstract service: IDataService<T>;

  constructor(
    public readonly accessGuard: AccessGuard,
    public readonly device: Platform,
    public readonly errorService: ErrorService,
    public readonly functionSwitchHelper: FunctionSwitchHelperService,
    public readonly location: Location,
    public readonly route: ActivatedRoute,
    public readonly router: Router,
    public readonly toastService: ToastService,
    public readonly toggleMenuHelper: ToggleMenuHelper,
  ) {
    super(
      accessGuard,
      device,
      errorService,
      functionSwitchHelper,
      location,
      route,
      router,
      toastService,
      toggleMenuHelper,
    );
  }

  /**
   * Each new instantiation loads a selected object, or creates a new one.
   * @param optionService - service handover
   * @param offline - offline mode
   * @param form - validation form handover
   * @deprecated - use method "init()" instead and use objectForm for data and validation handling (if remote use "init(true)")
   */
  async initializeDetail(offline: boolean = false): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const id = await this.getID();
      if (id === 'create') {
        this.object = this.newObject();
        resolve();
      } else {
        try {
          let res;
          if (offline) {
            res = await firstValueFrom(this.service.find(Number(id)));
          } else {
            res = await firstValueFrom(this.service.findRemote(Number(id)));
          }

          this.object = res;
          resolve();
        } catch (ex) {
          reject();
          this.errorService.handle(ex);
        }
      }
    });
  }

  /**
   * Each new instantiation loads a selected object, or creates a new one.
   * @param optionService - service handover
   * @param form - validation form handover
   * @deprecated - use method "init(true)" instead and use objectForm for data and validation handling
   */
  async initializeDetailRemote(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const id = await this.getID();
      if (id === 'create') {
        this.object = this.newObject();
        resolve();
      } else {
        this.service.findRemote(Number(id)).subscribe(
          res => {
            this.object = res;
            resolve();
          },
          err => {
            reject();
            this.errorService.handle(err);
          },
        );
      }
    });
  }

  /**
   * If a new entry is to be created, the appropriate object is created here.
   */
  newObject(): T {
    return {} as T;
  }

  // ************************************************************************************************
  // ** ALL NEW DETAIL SERVICE ONLY WITH REACTIVEFORMS **********************************************
  // ************************************************************************************************

  /** Gets the ID of the selected item of the index-page */
  async getID(): Promise<string | null> {
    return (await firstValueFrom(this.route.paramMap)).get('id');
  }

  /**
   * Each new instantiation needs a form to validate and manage the data.
   */
  public initForm(obj?: T): void {
    console.debug('Should be overwritten');
  }

  /**
   * Get executed before saving the object. Can be used to manipulate the object before saving.
   * @returns error if the save process should get aborted with error string.
   */
  public async beforeSave(): Promise<string | void> {
    return;
  }

  /**
   * Returns true if the object was created.
   */
  public get _objectCreated(): boolean {
    return this.objectForm && !!this.objectForm?.get('id')?.value;
  }

  /**
   * Initializes the Detail-Page with the ID of the selected item and sets the form-object.
   */
  async init(remote?: boolean): Promise<T | undefined> {
    const id = await this.getID();
    if (id && id != 'create') {
      let res;
      if (remote) {
        res = await firstValueFrom(this.service.findRemote(Number(id))).catch(
          err => {
            this.errorService.handle(err);
          },
        );
      } else {
        res = await firstValueFrom(this.service.find(Number(id))).catch(err => {
          this.errorService.handle(err);
        });
      }
      this.initForm(res);
      this.objectForm.reset();
      this.objectForm.patchValue(res);
      this._initialized = true;
      return res;
    } else {
      this.initForm(this.newObject());
      this._initialized = true;
      return undefined;
    }
  }
  /**
   * Saves the object and navigates back to the index-page.
   * @param navIndex True if the index-page should be navigated to after saving.
   * @returns saved object
   */
  async save(navIndex?: boolean, remote?: boolean): Promise<T | undefined> {
    const err = await this.beforeSave();
    if (err) {
      this.toastService.danger(err);
      return;
    }
    // DataService (z.B. DefaultLoadlistService) hat kein saveRemote, also prüfen
    const value = this.objectForm.getRawValue();
    const offlineService = this.service as OfflineDataService<any>;
    const serv = remote && typeof offlineService.saveRemote === 'function'
      ? offlineService.saveRemote(value)
      : this.service.save(value, null);
    const savedObj = await firstValueFrom(serv).catch(err => {
      this.errorService.handle(err);
    });
    if (savedObj && !remote) {
      if (typeof offlineService.saveRemote !== 'function') {
        throw new Error('Service must extend OfflineDataService');
      }
      await(this.service as OfflineDataService<any>).saveLocalForce(savedObj);
    }
    if (navIndex) {
      this.objectForm.reset();
      this.router.navigate(['../../'], {
        relativeTo: this.route,
        queryParams: { reloadMitsTable: true },
      });
    }
    this.afterSave();
    return savedObj;
  }

  afterSave(): void {
    return;
  }
}
